import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, RefreshCw, FileSpreadsheet, CheckCircle2, AlertTriangle,
         X, ChevronDown, Clock, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useUsage, refreshUsage } from "@/hooks/useUsage";
import { UpgradeModal } from "@/components/UsageWidget";

/**
 * WeeklyAnalysisCard — Step 5 of the dashboard journey.
 *
 * Three states based on `next_analysis_date`:
 *   • countdown     → "Наступний аналіз через X днів", button still available
 *                     with hint that running early consumes one quota.
 *   • ready         → big "Час оновити аналіз!" call-to-action.
 *   • exhausted     → opens UpgradeModal on click (quota burned for the period).
 *
 * Two parallel ways to source data: CSV drag/drop or live MCC auto-pull.
 *
 * Pipeline:
 *   POST /api/reports/upload-csv  (only for CSV path)
 *   POST /api/reports/analyze     (background job → returns report_id)
 *   GET  /api/reports/{id}        (poll every 4s until status=done)
 *   Toast + navigate(/reports/{id})
 */
export default function WeeklyAnalysisCard({ nextAnalysisAt, lastAnalysisAt }) {
    const navigate = useNavigate();
    const { getLimit } = useUsage();
    const csvLimit = getLimit("csv_analyses");

    const [liveStatus, setLiveStatus] = useState(null);   // {available, reason, customer_id}
    const [showUpgrade, setShowUpgrade] = useState(false);
    const [phase, setPhase] = useState("idle");           // idle | uploading | parsing | analyzing | finalizing | done | error

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/reports/live-status");
                setLiveStatus(r.data);
            } catch {
                setLiveStatus({ available: false, reason: "unknown" });
            }
        })();
    }, []);

    // ── countdown logic ───────────────────────────────────────────────────
    const now = Date.now();
    const next = nextAnalysisAt ? new Date(nextAnalysisAt).getTime() : null;
    const diffMs = next ? next - now : null;
    const isReady = !next || diffMs <= 0;
    const daysLeft = diffMs != null && diffMs > 0 ? Math.ceil(diffMs / 86_400_000) : 0;

    const exhausted = csvLimit?.isExhausted;

    // ── handler when analysis is kicked off ───────────────────────────────
    async function runAnalysis({ source, upload_id, days }) {
        if (exhausted) { setShowUpgrade(true); return; }
        setPhase("analyzing");
        try {
            const r = await api.post("/reports/analyze", { source, upload_id, days });
            const id = r.data.report_id;
            await pollUntilDone(id);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (e?.response?.status === 402) {
                setShowUpgrade(true);
                setPhase("idle");
                return;
            }
            toast.error(typeof detail === "string" ? detail : "Не вдалося запустити аналіз");
            setPhase("error");
            setTimeout(() => setPhase("idle"), 3000);
        }
    }

    async function pollUntilDone(reportId) {
        setPhase("analyzing");
        // Visual phase progression while AI works (cosmetic).
        const phases = ["parsing", "analyzing", "finalizing"];
        let phaseIdx = 0;
        const phaseTimer = setInterval(() => {
            phaseIdx = Math.min(phaseIdx + 1, phases.length - 1);
            setPhase(phases[phaseIdx]);
        }, 25_000);

        try {
            for (let i = 0; i < 60; i++) {           // up to 4min
                await new Promise(res => setTimeout(res, 4_000));
                const r = await api.get(`/reports/${reportId}`);
                if (r.data.status === "done") {
                    clearInterval(phaseTimer);
                    setPhase("done");
                    await refreshUsage();
                    toast.success("Звіт готовий!");
                    setTimeout(() => navigate(`/reports/${reportId}`), 700);
                    return;
                }
                if (r.data.status === "error") {
                    clearInterval(phaseTimer);
                    setPhase("error");
                    toast.error(r.data.error || "AI-аналіз завершився з помилкою");
                    setTimeout(() => setPhase("idle"), 4000);
                    return;
                }
            }
            clearInterval(phaseTimer);
            toast.warning("Аналіз триває довше ніж очікувалось — перевірте сторінку «Звіти» пізніше.");
            setPhase("idle");
        } catch (e) {
            clearInterval(phaseTimer);
            toast.error("Помилка отримання звіту: " + (e?.message || "невідомо"));
            setPhase("error");
            setTimeout(() => setPhase("idle"), 4000);
        }
    }

    if (["parsing", "analyzing", "finalizing"].includes(phase)) {
        return <ProgressView phase={phase} />;
    }

    return (
        <section data-testid="weekly-analysis-card" className={`glass rounded-2xl p-7 ${isReady ? "border border-primary/40" : ""}`}>
            <Header isReady={isReady} daysLeft={daysLeft} lastAnalysisAt={lastAnalysisAt} csvLimit={csvLimit} />

            {exhausted && <ExhaustedBanner csvLimit={csvLimit} onUpgrade={() => setShowUpgrade(true)} />}

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
                <CsvDropZone
                    disabled={exhausted}
                    onAnalyze={(upload_id) => runAnalysis({ source: "csv", upload_id, days: 7 })}
                    setBusy={(b) => setPhase(b ? "uploading" : "idle")}
                />
                {liveStatus?.available ? (
                    <LiveCard
                        disabled={exhausted}
                        customerId={liveStatus.customer_id}
                        onAnalyze={(days) => runAnalysis({ source: "live", days })}
                    />
                ) : (
                    <LinkMccHint reason={liveStatus?.reason} />
                )}
            </div>

            <HowToExportCsv />

            {showUpgrade && (
                <UpgradeModal
                    currentPlan={csvLimit?.currentPlan}
                    reason={`Ліміт CSV-аналізів вичерпано (${csvLimit?.used}/${csvLimit?.limit}) на тарифі ${(csvLimit?.currentPlan || "starter").toUpperCase()}. Перейдіть на вищий тариф або зачекайте до скидання.`}
                    onClose={() => setShowUpgrade(false)}
                />
            )}
        </section>
    );
}


// ─── HEADER ───────────────────────────────────────────────────────────────
function Header({ isReady, daysLeft, lastAnalysisAt, csvLimit }) {
    return (
        <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
                <h2 className="font-display font-bold text-xl text-text-primary flex items-center gap-2" data-testid="wac-title">
                    {isReady ? "Час оновити аналіз!" : "Аналіз даних"}
                </h2>
                <p className="mt-1 text-sm text-secondary-c">
                    {isReady
                        ? "Дані готові. Залийте CSV або скористайтесь автопідключенням Google Ads."
                        : (
                            <>Наступний аналіз через <span className="text-primary-c font-mono font-semibold" data-testid="wac-days">{daysLeft}</span> {daysLeft === 1 ? "день" : "днів"} <span className="text-faint-c">— але можна запустити вже зараз.</span></>
                        )}
                </p>
                {lastAnalysisAt && (
                    <p className="mt-1 text-xs text-faint-c">
                        Останній аналіз: {new Date(lastAnalysisAt).toLocaleString("uk-UA", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                )}
            </div>
            {csvLimit && (
                <div className="text-xs text-secondary-c text-right" data-testid="wac-quota">
                    <div>Цього місяця</div>
                    <div className="font-mono text-text-primary text-sm">{csvLimit.used} / {csvLimit.limit} аналізів</div>
                </div>
            )}
        </div>
    );
}


function ExhaustedBanner({ csvLimit, onUpgrade }) {
    return (
        <div className="mt-4 flex items-center gap-3 p-3 rounded-lg border border-warning/30 bg-warning/10" data-testid="wac-exhausted">
            <Lock size={16} className="text-warning-c shrink-0" />
            <div className="text-sm text-text-primary flex-1">
                Ви використали всі {csvLimit?.limit} CSV-аналізів цього місяця.
            </div>
            <button onClick={onUpgrade} className="text-sm font-semibold text-primary-c hover:underline" data-testid="wac-upgrade-btn">
                Розглянути апгрейд →
            </button>
        </div>
    );
}


// ─── CSV DROP ZONE ────────────────────────────────────────────────────────
function CsvDropZone({ disabled, onAnalyze, setBusy }) {
    const fileInput = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [files, setFiles] = useState([]);          // [{file_name, report_type, row_count, error, warnings}]
    const [uploadId, setUploadId] = useState(null);
    const [uploading, setUploading] = useState(false);

    async function handleFiles(fileList) {
        if (!fileList || !fileList.length) return;
        const csvLike = Array.from(fileList).filter(
            f => f.size > 0 && (f.name.toLowerCase().endsWith(".csv") || f.type.includes("csv") || f.type.includes("text"))
        );
        if (!csvLike.length) {
            toast.error("Підтримуються лише CSV-файли з Google Ads.");
            return;
        }
        setUploading(true); setBusy(true);
        try {
            const fd = new FormData();
            csvLike.forEach(f => fd.append("files", f, f.name));
            const r = await api.post("/reports/upload-csv", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setUploadId(r.data.upload_id);
            setFiles(r.data.files);
            const accepted = r.data.accepted_files;
            const rejected = r.data.rejected_files;
            if (accepted === 0) {
                toast.error("Жоден файл не прийнято — перевірте, що це CSV з Google Ads.");
            } else {
                toast.success(`Прийнято файлів: ${accepted}${rejected ? ` (відхилено: ${rejected})` : ""}`);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Не вдалося завантажити файли");
        } finally {
            setUploading(false); setBusy(false);
        }
    }

    function onDrop(e) {
        e.preventDefault(); setDragging(false);
        if (!disabled) handleFiles(e.dataTransfer.files);
    }

    return (
        <div data-testid="wac-csv-zone" className="rounded-2xl border border-border bg-surface/40 p-5 flex flex-col">
            <div className="flex items-center gap-2">
                <FileSpreadsheet size={18} className="text-primary-c" />
                <h3 className="font-display font-semibold text-base text-text-primary">Залити CSV</h3>
            </div>

            {!files.length ? (
                <button
                    type="button"
                    disabled={disabled || uploading}
                    onClick={() => fileInput.current?.click()}
                    onDragOver={e => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    data-testid="wac-csv-dropzone"
                    className={`mt-4 flex-1 min-h-[180px] rounded-xl border-2 border-dashed transition flex flex-col items-center justify-center gap-2 px-6 text-center
                        ${dragging ? "border-primary bg-primary/10" : "border-border hover:border-primary/40 hover:bg-surface-hover/40"}
                        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                    {uploading
                        ? <Loader2 size={32} className="text-primary-c animate-spin" />
                        : <UploadCloud size={32} className="text-secondary-c" />}
                    <div className="text-sm text-text-primary font-medium">
                        {uploading ? "Завантаження..." : "Перетягніть CSV-звіти або натисніть, щоб обрати"}
                    </div>
                    <div className="text-xs text-faint-c max-w-xs">
                        Підтримуємо: загальний по кампаніях, ad groups, пошукові запити, ключі.
                    </div>
                    <input
                        ref={fileInput}
                        type="file"
                        multiple
                        accept=".csv,text/csv"
                        className="hidden"
                        onChange={e => handleFiles(e.target.files)}
                        data-testid="wac-csv-input"
                    />
                </button>
            ) : (
                <div className="mt-4 flex-1 flex flex-col gap-2" data-testid="wac-csv-preview">
                    {files.map((f, i) => (
                        <div key={i} className={`flex items-center gap-3 p-3 rounded-lg border ${f.error ? "border-error/30 bg-error/5" : "border-border bg-surface-hover/40"}`}>
                            {f.error ? <AlertTriangle size={16} className="text-error-c shrink-0" />
                                     : <CheckCircle2 size={16} className="text-success-c shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <div className="text-sm text-text-primary font-medium truncate">{f.file_name}</div>
                                <div className="text-xs text-secondary-c truncate">
                                    {f.error ? f.error : `${REPORT_TYPE_LABELS[f.report_type] || f.report_type} · ${f.row_count} ряд.`}
                                </div>
                                {(f.warnings || []).slice(0, 2).map((w, j) => (
                                    <div key={j} className="text-[11px] text-warning-c mt-0.5">⚠ {w}</div>
                                ))}
                            </div>
                        </div>
                    ))}
                    <div className="flex items-center gap-2 mt-2">
                        <button
                            onClick={() => { setFiles([]); setUploadId(null); }}
                            className="text-xs text-secondary-c hover:text-text-primary"
                            data-testid="wac-csv-reset"
                        >
                            <X size={12} className="inline" /> Скинути
                        </button>
                        <button
                            disabled={disabled || !uploadId || files.every(f => f.error)}
                            onClick={() => onAnalyze(uploadId)}
                            className="ml-auto inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-40"
                            data-testid="wac-csv-analyze-btn"
                        >
                            Запустити аналіз →
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

const REPORT_TYPE_LABELS = {
    campaigns:    "Звіт по кампаніях",
    ad_groups:    "Звіт по групах оголошень",
    keywords:     "Звіт по ключових словах",
    search_terms: "Звіт по пошукових запитах",
    unknown:      "Невідомий тип (AI розбереться)",
};


// ─── LIVE PULL CARD ───────────────────────────────────────────────────────
function LiveCard({ disabled, customerId, onAnalyze }) {
    const [days, setDays] = useState(7);
    return (
        <div data-testid="wac-live-card" className="rounded-2xl border border-primary/30 bg-primary/5 p-5 flex flex-col">
            <div className="flex items-center gap-2">
                <RefreshCw size={18} className="text-primary-c" />
                <h3 className="font-display font-semibold text-base text-text-primary">Витягнути дані автоматично</h3>
                <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-primary-c bg-primary/15 px-2 py-0.5 rounded">Premium</span>
            </div>
            <p className="mt-3 text-sm text-secondary-c">
                Ваш Google Ads <span className="font-mono text-text-primary">{customerId}</span> підключений — заберемо дані без CSV.
            </p>
            <div className="mt-5 flex-1">
                <div className="text-xs text-secondary-c mb-2">Період аналізу</div>
                <div className="grid grid-cols-3 gap-2" data-testid="wac-live-period">
                    {[7, 14, 30].map(d => (
                        <button
                            key={d}
                            type="button"
                            onClick={() => setDays(d)}
                            data-testid={`wac-live-${d}d`}
                            className={`px-3 py-2 rounded-lg text-sm font-medium border transition ${
                                days === d
                                    ? "border-primary bg-primary text-white"
                                    : "border-border bg-surface/30 text-secondary-c hover:text-text-primary hover:border-primary/40"
                            }`}
                        >
                            {d} {d === 1 ? "день" : "днів"}
                        </button>
                    ))}
                </div>
            </div>
            <button
                disabled={disabled}
                onClick={() => onAnalyze(days)}
                className="mt-5 inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-40"
                data-testid="wac-live-analyze-btn"
            >
                <RefreshCw size={14} /> Запустити автоматичний аналіз
            </button>
        </div>
    );
}


function LinkMccHint({ reason }) {
    const navigate = useNavigate();
    return (
        <div data-testid="wac-link-mcc-hint" className="rounded-2xl border border-dashed border-border bg-surface/20 p-5 flex flex-col items-start justify-center min-h-[180px]">
            <p className="text-sm text-secondary-c">
                {reason === "pending"
                    ? "MCC-зв’язок ще не активний — прийміть запрошення у вашому Google Ads, щоб увімкнути автозбір."
                    : "Підключіть Google Ads, щоб витягувати звіти автоматично без CSV."}
            </p>
            <button
                onClick={() => navigate("/onboarding/step-1")}
                className="mt-3 text-sm text-primary-c hover:underline font-medium"
                data-testid="wac-link-mcc-btn"
            >
                Підключити Google Ads →
            </button>
        </div>
    );
}


// ─── COLLAPSIBLE "HOW TO EXPORT CSV" ─────────────────────────────────────
function HowToExportCsv() {
    const [open, setOpen] = useState(false);
    return (
        <div className="mt-6 border-t border-border pt-5">
            <button
                onClick={() => setOpen(o => !o)}
                className="flex items-center gap-2 text-sm text-secondary-c hover:text-text-primary transition"
                data-testid="wac-howto-toggle"
            >
                <ChevronDown size={14} className={`transition ${open ? "rotate-180" : ""}`} />
                Як експортувати CSV з Google Ads?
            </button>
            {open && (
                <ol className="mt-4 ml-1 space-y-2 text-sm text-secondary-c list-decimal list-inside" data-testid="wac-howto-content">
                    <li>В Google Ads відкрийте розділ <span className="text-text-primary">«Кампанії»</span>.</li>
                    <li>У верхньому правому куті натисніть значок <span className="text-text-primary">завантаження ⬇</span> та оберіть формат <span className="text-text-primary">CSV</span>.</li>
                    <li>Повторіть для розділів <span className="text-text-primary">«Групи оголошень»</span>, <span className="text-text-primary">«Ключові слова»</span> та <span className="text-text-primary">«Пошукові запити»</span> (Search Terms). Залийте всі сюди.</li>
                </ol>
            )}
        </div>
    );
}


// ─── PROGRESS VIEW (while AI runs) ────────────────────────────────────────
// PR #52 redesign: the old version had three problems:
//   1. Progress bar jumped 25% → 60% → 90% in steps — felt fake/janky.
//   2. animate-pulse on the bar itself looked broken (shimmer + width
//      transition fighting each other).
//   3. No sense of "something is actually happening" — static bullets,
//      no time elapsed, no rotating status. 30-90s of staring at a
//      barely-moving bar feels like the app froze.
//
// New design:
//   • Smooth asymptotic progress bar (eases toward 95%, never hits 100%
//     until status=done). Updates every 200ms via requestAnimationFrame-
//     friendly setInterval.
//   • Elapsed seconds counter ("00:23") in monospace — concrete signal
//     that time IS passing.
//   • Rotating status lines under the title ("Читаю ключові слова...",
//     "Перевіряю search terms...", "Згруповую за нішею..." etc) — gives
//     the impression the AI is doing detailed work, not just spinning.
//   • Stepper with explicit completed (✓) / active (pulse) / pending (○)
//     states — clear which of the 3 stages we're in.
//   • Larger pulsing brand-coloured "core" instead of a tiny corner
//     spinner — owns the visual weight of a 60-second wait.

const PHASE_META = {
    parsing:    {
        title: "Парсимо дані…",
        sub:   "Розбираємо CSV і збираємо метрики.",
        rotating: [
            "Читаю CSV-файли…",
            "Витягую кампанії та ad groups…",
            "Збираю search terms…",
            "Перевіряю currency і дати…",
        ],
    },
    analyzing:  {
        title: "Аналізуємо метрики…",
        sub:   "AI читає кампанії, ключі, пошукові запити.",
        rotating: [
            "Перевіряю CTR по групах…",
            "Шукаю keywords з низькою конверсією…",
            "Звіряю реальність зі стратегією…",
            "Аналізую search terms на irrelevant запити…",
            "Перевіряю чи tracking працює…",
            "Виявляю де згоряє бюджет…",
        ],
    },
    finalizing: {
        title: "Готуємо рекомендації…",
        sub:   "Збираємо звіт і пишемо план дій.",
        rotating: [
            "Розставляю пріоритети…",
            "Формую прогноз на наступний тиждень…",
            "Перевіряю чи рекомендації не нашкодять core keywords…",
            "Оцінюю загальний health score…",
            "Майже готово…",
        ],
    },
};

const PHASE_ORDER = ["parsing", "analyzing", "finalizing"];

/**
 * ProgressView — full-card layout while the AI report is being generated.
 *
 * Visual hierarchy (top to bottom):
 *   ┌─────────────────────────────────────────────┐
 *   │  ●─pulse  Title (current phase)    00:23    │
 *   │           Rotating status line              │
 *   │           ━━━━━━━━━━━━━━━━━━━━━━ 67%        │
 *   │           ✓ Парсимо  ◉ Аналізуємо  ○ Звіт   │
 *   │           ⓘ Зазвичай 30-90 секунд.          │
 *   └─────────────────────────────────────────────┘
 */
const ProgressView = ({ phase }) => {
    const meta = PHASE_META[phase] || PHASE_META.parsing;

    // ── Smooth progress (asymptotic, never quite reaches 100%) ──────────
    // We don't have real backend progress signals, so we model expected
    // time = 60s and use 1 - exp(-elapsed / 30) which rises fast at first
    // then crawls — feels honest because users perceive deceleration as
    // "the hard part is happening now".
    const [progress, setProgress] = useState(0);
    const [elapsed, setElapsed]   = useState(0);
    const [rotatingIdx, setRotatingIdx] = useState(0);
    const startedAtRef = useRef(Date.now());

    // Reset on phase change so the rotating line picks the new phase's set
    useEffect(() => { setRotatingIdx(0); }, [phase]);

    // Tick: every 200ms update elapsed + progress
    useEffect(() => {
        const tick = setInterval(() => {
            const sec = (Date.now() - startedAtRef.current) / 1000;
            setElapsed(sec);
            // Asymptote at 95%; reaches ~63% at 30s, ~86% at 60s, ~95% at 90s.
            const p = 95 * (1 - Math.exp(-sec / 30));
            setProgress(p);
        }, 200);
        return () => clearInterval(tick);
    }, []);

    // Rotate status lines every 4s within the current phase
    useEffect(() => {
        const lines = meta.rotating || [];
        if (lines.length <= 1) return;
        const rot = setInterval(() => {
            setRotatingIdx(i => (i + 1) % lines.length);
        }, 4000);
        return () => clearInterval(rot);
    }, [meta]);

    const currentIdx = PHASE_ORDER.indexOf(phase);
    const mm = String(Math.floor(elapsed / 60)).padStart(2, "0");
    const ss = String(Math.floor(elapsed % 60)).padStart(2, "0");

    return (
        <section data-testid="wac-progress" className="glass rounded-2xl p-7 border border-primary/30 relative overflow-hidden">
            {/* Subtle background gradient — adds depth without distracting */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5 pointer-events-none" />

            <div className="relative flex items-start gap-5">
                {/* Pulsing core — bigger visual anchor than a small spinner */}
                <div className="shrink-0 mt-1 relative">
                    <div className="w-12 h-12 rounded-full bg-primary/15 animate-ping absolute inset-0" />
                    <div className="w-12 h-12 rounded-full bg-primary/25 flex items-center justify-center relative">
                        <Loader2 size={20} className="text-primary-c animate-spin" />
                    </div>
                </div>

                <div className="flex-1 min-w-0">
                    {/* Title + elapsed time on same row */}
                    <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <h2
                            className="font-display font-bold text-xl text-text-primary"
                            data-testid="wac-progress-title"
                        >
                            {meta.title}
                        </h2>
                        <div className="font-mono text-sm text-secondary-c tabular-nums" data-testid="wac-progress-elapsed">
                            {mm}:{ss}
                        </div>
                    </div>

                    {/* Static subtitle */}
                    <p className="mt-1 text-sm text-secondary-c">{meta.sub}</p>

                    {/* Rotating status line — gives sense of detailed work */}
                    {meta.rotating && meta.rotating.length > 0 && (
                        <p
                            key={`${phase}-${rotatingIdx}`}
                            className="mt-2 text-xs font-mono text-primary-c/80 animate-[fadeIn_0.5s_ease]"
                            data-testid="wac-progress-rotating"
                        >
                            › {meta.rotating[rotatingIdx]}
                        </p>
                    )}

                    {/* Smooth progress bar with shimmer */}
                    <div className="mt-4 h-2 bg-surface-hover rounded-full overflow-hidden relative">
                        <div
                            className="h-full bg-gradient-to-r from-primary/80 to-primary rounded-full relative overflow-hidden"
                            style={{ width: `${progress}%`, transition: "width 0.3s ease-out" }}
                        >
                            {/* Moving shimmer inside the filled portion */}
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-[shimmer_2s_linear_infinite]"
                                 style={{ backgroundSize: "200% 100%" }} />
                        </div>
                    </div>

                    {/* Stepper — clear which step is active */}
                    <div className="mt-4 flex items-center gap-2 flex-wrap">
                        {PHASE_ORDER.map((p, i) => {
                            const labels = { parsing: "Парсимо", analyzing: "Аналізуємо", finalizing: "Рекомендації" };
                            const isDone   = i < currentIdx;
                            const isActive = i === currentIdx;
                            return (
                                <React.Fragment key={p}>
                                    <StepperPill state={isDone ? "done" : isActive ? "active" : "pending"} label={labels[p]} />
                                    {i < PHASE_ORDER.length - 1 && (
                                        <span className={`text-xs ${i < currentIdx ? "text-primary-c" : "text-faint-c"}`}>›</span>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>

                    {/* Reassurance footer */}
                    <p className="mt-4 text-xs text-faint-c flex items-center gap-1.5">
                        <Clock size={11} /> Зазвичай 30-90 секунд. Можете залишитись на сторінці — звіт відкриється автоматично.
                    </p>
                </div>
            </div>

            {/* Local keyframes injected inline — avoids touching tailwind.config.js */}
            <style>{`
                @keyframes shimmer {
                    0%   { transform: translateX(-100%); }
                    100% { transform: translateX(100%);  }
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(2px); }
                    to   { opacity: 1; transform: translateY(0);   }
                }
            `}</style>
        </section>
    );
};

/**
 * StepperPill — one bullet in the 3-step progress indicator.
 *   • done    → check icon + primary tint
 *   • active  → pulsing dot + bold label
 *   • pending → empty circle + faint label
 */
const StepperPill = ({ state, label }) => {
    if (state === "done") {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono text-primary-c">
                <CheckCircle2 size={13} className="text-primary-c" />
                {label}
            </span>
        );
    }
    if (state === "active") {
        return (
            <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono text-primary-c font-semibold">
                <span className="relative flex w-2.5 h-2.5">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-50 animate-ping" />
                    <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-primary" />
                </span>
                {label}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono text-faint-c">
            <span className="w-2.5 h-2.5 rounded-full border border-faint-c/50" />
            {label}
        </span>
    );
};